/**
 * Body for the "Your own domain" option. Input + primary action; once a
 * domain is saved (pending or verified), the DNS records panel follows
 * inside this same body — nested, not a page-level sibling card.
 */
import React, { useCallback, useEffect, useState } from "react";
import Button from "../../../theme/untitled/Button/Button";
import Icon from "../../../theme/untitled/icons/Icon";
import FieldError from "./FieldError";
import DnsPanel from "./DnsPanel";
import { parseGeneralError } from "./errorHelpers";
import styles from "./CustomDomainOption.module.scss";

const DOMAIN_PATTERN = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;

const CustomDomainOption = ({
  pendingDomain,
  domain,
  onRequestSubmit,
  isLoading,
  error,
  dnsSettings,
  isRefreshing,
  isRegenerating,
  isDisconnecting,
  onRefreshDnsStatus,
  onRegenerate,
  onRequestDisconnect,
  refreshError,
  regenerateError,
  disconnectError,
}) => {
  const savedDomain = pendingDomain || domain || "";
  const [domainText, setDomainText] = useState(savedDomain);

  useEffect(() => {
    setDomainText(savedDomain);
  }, [savedDomain]);

  const hasConnectedDomain = !!savedDomain;
  const isDirty = domainText !== savedDomain;
  const isValid = DOMAIN_PATTERN.test(domainText.toLowerCase());
  const canSubmit = isDirty && isValid;

  const onDomainTextChange = useCallback((e) => {
    setDomainText(e.target.value.trim());
  }, []);

  const _onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!canSubmit) return;
      onRequestSubmit(domainText, hasConnectedDomain);
    },
    [canSubmit, domainText, hasConnectedDomain, onRequestSubmit]
  );

  const errorMessage = error
    ? parseGeneralError(error, "The service is unavailable. Try again in a moment.")
    : null;

  return (
    <>
      <div className={styles.row}>
        <input
          type="text"
          id="custom-domain"
          name="custom-domain"
          className={styles.input}
          placeholder="your-domain.com"
          disabled={isLoading}
          value={domainText}
          onChange={onDomainTextChange}
          aria-label="Domain"
        />
        <Button hierarchy="primary" onClick={_onSubmit} disabled={!canSubmit || isLoading} loading={isLoading}>
          {isLoading ? "Saving…" : hasConnectedDomain ? "Update domain" : "Set domain"}
        </Button>
      </div>

      <span className={styles.hint}>
        <Icon name="HelpCircle" size={14} className={styles.hintIcon} />
        No <code>https://</code> and no path — just the hostname.
      </span>

      <FieldError message={errorMessage} />

      {hasConnectedDomain && (
        <DnsPanel
          domain={domain}
          dnsSettings={dnsSettings}
          isRefreshing={isRefreshing}
          isRegenerating={isRegenerating}
          isDisconnecting={isDisconnecting}
          onRefreshDnsStatus={onRefreshDnsStatus}
          onRegenerate={onRegenerate}
          onRequestDisconnect={onRequestDisconnect}
          refreshError={refreshError}
          regenerateError={regenerateError}
          disconnectError={disconnectError}
        />
      )}
    </>
  );
};

export default CustomDomainOption;
